import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const statusBadge = (days_left) => {
    if (days_left === null || days_left === undefined) {
        return <Badge variant="secondary">Sin registro</Badge>;
    }
    if (days_left < 0) {
        return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Vencido hace {Math.abs(days_left)} d</Badge>;
    }
    if (days_left <= 30) {
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Vence en {days_left} d</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Vigente · {days_left} d</Badge>;
};

const DocCell = ({ doc }) => {
    if (!doc) {
        return <div className="text-xs text-slate-400">Sin registro</div>;
    }
    return (
        <div>
            <div className="text-xs text-slate-700">{fmtDate(doc.date)}</div>
            <div className="mt-1">{statusBadge(doc.days_left)}</div>
        </div>
    );
};

export default function Expirations() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/analytics/expirations")
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    }, []);

    const urgentCount = items.filter((i) => i.min_days_left !== null && i.min_days_left <= 30).length;

    return (
        <div className="space-y-6" data-testid="expirations-page">
            <header>
                <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Administrativo</p>
                <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1 font-display">Resumen de vencimientos</h1>
                <p className="text-sm text-slate-500 mt-1">Seguro, verificación y tarjeta de circulación de todos tus autos, ordenados por urgencia.</p>
            </header>

            {urgentCount > 0 && (
                <Card className="border-rose-200 bg-rose-50 shadow-sm" data-testid="expirations-urgent-banner">
                    <CardContent className="py-3 flex items-center gap-2 text-sm text-rose-700 font-medium">
                        <AlertTriangle size={16} />
                        {urgentCount} {urgentCount === 1 ? "auto tiene" : "autos tienen"} un documento vencido o por vencer en 30 días o menos
                    </CardContent>
                </Card>
            )}

            <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                        <CalendarClock size={18} className="text-slate-500" /> Vencimientos por auto
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-4 py-3 font-semibold">Auto</th>
                                    <th className="text-left px-4 py-3 font-semibold">Seguro</th>
                                    <th className="text-left px-4 py-3 font-semibold">Verificación</th>
                                    <th className="text-left px-4 py-3 font-semibold">Tarjeta de circulación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loading && items.length === 0 && (
                                    <tr><td colSpan={4} className="text-center px-4 py-10 text-slate-400">Aún no hay autos registrados.</td></tr>
                                )}
                                {items.map((i) => {
                                    const isUrgent = i.min_days_left !== null && i.min_days_left <= 30;
                                    return (
                                        <tr
                                            key={i.car_id}
                                            className={`border-t border-slate-100 ${isUrgent ? "border-l-2 border-l-rose-400" : ""}`}
                                            data-testid={`expiration-row-${i.car_id}`}
                                        >
                                            <td className="px-4 py-3">
                                                <Link to={`/autos/${i.car_id}`} className="font-medium text-slate-900 hover:underline">
                                                    {i.plate}
                                                </Link>
                                                <div className="text-xs text-slate-500">{i.brand} {i.model}</div>
                                            </td>
                                            <td className="px-4 py-3"><DocCell doc={i.insurance} /></td>
                                            <td className="px-4 py-3"><DocCell doc={i.verification} /></td>
                                            <td className="px-4 py-3"><DocCell doc={i.circulation_card} /></td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
